/**
 * PII masking + relative time helpers for the public /demo/leads view.
 *
 * Rules (per Phase 4 spec):
 *  - name  → "Maria H."        (first name + last initial)
 *  - email → "m***@example.com" (first char of local part + domain)
 *  - phone → "(239) ***-4141"   (middle digits masked, last 4 visible)
 *  - empty → "—"
 */

export function maskName(name) {
  if (!name || !name.trim()) return "\u2014";
  const trimmed = name.trim();
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0];
  const first = parts[0];
  const lastInitial = parts[parts.length - 1][0] || "";
  return `${first} ${lastInitial.toUpperCase()}.`;
}

export function maskEmail(email) {
  if (!email || !email.trim()) return "\u2014";
  const at = email.indexOf("@");
  if (at <= 0 || at === email.length - 1) return email;
  const localFirst = email[0];
  const domain = email.slice(at + 1);
  return `${localFirst}***@${domain}`;
}

export function maskPhone(phone) {
  if (!phone || !phone.trim()) return "\u2014";
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 7) return trimmed; // too short to mask safely

  const last4 = digits.slice(-4);
  const first3 = digits.slice(-10, -7) || digits.slice(0, 3);

  if (/^\(\d{3}\)\s*\d{3}[-\s]?\d{4}$/.test(trimmed)) {
    return trimmed.replace(/(\(\d{3}\)\s*)\d{3}([-\s]?\d{4})$/, "$1***$2");
  }
  if (/^\d{3}[-\s]\d{3}[-\s]\d{4}$/.test(trimmed)) {
    const sep = trimmed.includes("-") ? "-" : " ";
    return `${first3}${sep}***${sep}${last4}`;
  }
  if (digits.length === 10) {
    return `(${first3}) ***-${last4}`;
  }
  // Fallback: keep first 3 + last 4 visible
  return `${first3}***${last4}`;
}

export function formatRelativeTime(input) {
  if (!input) return "\u2014";
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return "\u2014";
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return date.toLocaleDateString();
}

export function formatAbsoluteTime(input) {
  if (!input) return "";
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}
